// This file is a service module for analytics data queries
// All analytics database operations are centralized here to maintain data access patterns

import { platformApiFetch } from '@/services/platformApi/client';

// Row-level shapes (minimal fields used by transformations)
interface ProviderImpressionRow { created_at: string; }
interface ProviderViewRow { created_at: string; provider_id?: string | null; }
interface ProviderContactRow { created_at: string; provider_id?: string | null; contact_type?: string | null; }
interface UserSessionRow { created_at: string; pages_visited?: number | null; session_duration_seconds?: number | null; }
interface SearchAnalyticsRow { created_at: string; search_query?: string | null; category?: string | null; }
interface ServiceProviderRow { id: string; name: string; category: string; }

export interface AnalyticsData {
  totalUsers: number;
  totalListings: number;
  totalReviews: number;
  totalSubscriptions: number;
  totalViews: number;
  totalContacts: number;
  totalImpressions: number;
  totalSearches: number;
  totalSessions: number;
}

export interface TimeRangeAnalytics extends AnalyticsData {
  viewsByDay: Array<{ date: string; views: number }>;
  contactsByType: Array<{ type: string; count: number }>;
  topProviders: Array<{
    name: string;
    category: string;
    contacts: number;
    views: number;
    totalEngagement: number;
  }>;
  popularCategories: Array<{
    category: string;
    totalEngagement: number;
    providerCount: number;
  }>;
  sessionMetrics: {
    avgPagesVisited: number;
    avgSessionDuration: number;
  };
  clickThroughRate: string;
  whatsappContacts: number;
  otherContacts: number;
  searchesByDay: Array<{ date: string; searches: number }>;
  topSearchTerms: Array<{ term: string; count: number }>;
}

// Get basic analytics counts (used by dashboard)
export async function getBasicAnalytics(): Promise<AnalyticsData> {
  try {
    return await platformApiFetch<AnalyticsData>('/v1/admin/analytics');
  } catch (error) {
    console.error('Error fetching basic analytics:', error);
    return {
      totalUsers: 0,
      totalListings: 0,
      totalReviews: 0,
      totalSubscriptions: 0,
      totalViews: 0,
      totalContacts: 0,
      totalImpressions: 0,
      totalSearches: 0,
      totalSessions: 0,
    };
  }
}

// Get time-range analytics (used by analytics page)
export async function getTimeRangeAnalytics(days: number): Promise<TimeRangeAnalytics> {
  try {
    return await platformApiFetch<TimeRangeAnalytics>(`/v1/admin/analytics?days=${encodeURIComponent(String(days))}`);
  } catch (error) {
    console.error('Error fetching time range analytics:', error);
    const basicStats = await getBasicAnalytics();
    return {
      ...basicStats,
      viewsByDay: [],
      searchesByDay: [],
      contactsByType: [],
      topProviders: [],
      popularCategories: [],
      sessionMetrics: { avgPagesVisited: 0, avgSessionDuration: 0 },
      clickThroughRate: '0.00',
      whatsappContacts: 0,
      otherContacts: 0,
      topSearchTerms: [],
    };
  }
}

function processDataByDay<T extends { created_at: string }>(data: T[], days: number) {
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];

    const count = data.filter(item =>
      item.created_at.startsWith(dateStr)
    ).length;

    result.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      views: count,
      searches: count // For searchesByDay, this will be search count
    });
  }
  return result;
}

function processContactsByType(data: ProviderContactRow[]) {
  const types = data.reduce<Record<string, number>>((acc, item) => {
    const type = item.contact_type || 'unknown';
    acc[type] = (acc[type] ?? 0) + 1;
    return acc;
  }, {});

  return Object.entries(types).map(([type, count]) => ({
    type: type.charAt(0).toUpperCase() + type.slice(1),
    count: count as number
  }));
}

function processSessionMetrics(data: UserSessionRow[]) {
  if (data.length === 0) {
    return { avgPagesVisited: 0, avgSessionDuration: 0 };
  }

  const avgPagesVisited = data.reduce((sum, session) => sum + (session.pages_visited || 0), 0) / data.length;
  const avgSessionDuration = data
    .filter(session => session.session_duration_seconds != null)
    .reduce((sum, session) => sum + (session.session_duration_seconds || 0), 0) / 
    Math.max(1, data.filter(session => session.session_duration_seconds != null).length);

  return {
    avgPagesVisited: Math.round(avgPagesVisited * 10) / 10,
    avgSessionDuration: Math.round(avgSessionDuration / 60 * 10) / 10 // Convert to minutes
  };
}

function processTopProviders(
  contactsData: ProviderContactRow[],
  viewsData: ProviderViewRow[],
  serviceProvidersData: ServiceProviderRow[]
) {
  const providerStats: Record<string, {
    name: string;
    category: string;
    contacts: number;
    views: number;
  }> = {};

  // Create a map of provider info
  const providerMap = serviceProvidersData.reduce((acc, provider) => {
    acc[provider.id] = {
      name: provider.name,
      category: provider.category
    };
    return acc;
  }, {} as Record<string, { name: string; category: string }>);

  // Count contacts per provider
  contactsData.forEach(item => {
    const providerId = item.provider_id;
    if (providerId && providerMap[providerId]) {
      if (!providerStats[providerId]) {
        providerStats[providerId] = {
          name: providerMap[providerId].name,
          category: providerMap[providerId].category,
          contacts: 0,
          views: 0
        };
      }
      providerStats[providerId].contacts += 1;
    }
  });

  // Count views per provider
  viewsData.forEach(item => {
    const providerId = item.provider_id;
    if (providerId && providerMap[providerId]) {
      if (!providerStats[providerId]) {
        providerStats[providerId] = {
          name: providerMap[providerId].name,
          category: providerMap[providerId].category,
          contacts: 0,
          views: 0
        };
      }
      providerStats[providerId].views += 1;
    }
  });

  // Calculate engagement and return top providers
  const providers = Object.values(providerStats).map(provider => ({
    ...provider,
    totalEngagement: (provider.contacts * 3) + provider.views
  }));

  return providers
    .sort((a, b) => b.totalEngagement - a.totalEngagement)
    .slice(0, 10);
}

function processPopularCategories(topProviders: Array<{
  name: string;
  category: string;
  contacts: number;
  views: number;
  totalEngagement: number;
}>): Array<{ category: string; totalEngagement: number; providerCount: number }> {
  const categoryStats: Record<string, {
    category: string;
    totalEngagement: number;
    providerCount: number;
  }> = {};

  topProviders.forEach(provider => {
    if (provider.category) {
      if (!categoryStats[provider.category]) {
        categoryStats[provider.category] = {
          category: provider.category,
          totalEngagement: 0,
          providerCount: 0
        };
      }
      categoryStats[provider.category].totalEngagement += provider.totalEngagement;
      categoryStats[provider.category].providerCount += 1;
    }
  });

  return Object.values(categoryStats)
    .sort((a, b) => b.totalEngagement - a.totalEngagement)
    .slice(0, 8);
}

function processTopSearchTerms(data: SearchAnalyticsRow[]): Array<{ term: string; count: number }> {
  const termCounts = data.reduce((acc, item) => {
    if (item.search_query && item.search_query.trim()) {
      const term = item.search_query.trim().toLowerCase();
      acc[term] = (acc[term] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(termCounts)
    .map(([term, count]) => ({ term, count: count as number }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}
